import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import signalRService from '../api/SignalRService';
import { useUserData } from './useUserData';

export const useNotifications = () => {
    const { accountId, isValidator, isValidationManager } = useUserData();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);

    // Connexion initiale à SignalR
    useEffect(() => {
        const connectSignalR = async () => {
            if (!accountId) {
                // console.log('SignalR: AccountId manquant, connexion différée');
                return;
            }

            try {
                // console.log('SignalR: AccountId utilisé:', accountId);
                // console.log('Type:', typeof accountId);
                // console.log('SignalR: Tentative de connexion pour accountId:', accountId);
                
                // CORRECTION: La connexion appelle maintenant RegisterUser automatiquement
                await signalRService.connect(accountId);
                
                setIsConnected(true);
                setConnectionError(null);
                // console.log('SignalR: Connecté avec succès');

                // CORRECTION: Rejoindre les groupes APRÈS l'enregistrement
                // Le service appelle déjà RegisterUser dans connect()
                
                if (isValidator || isValidationManager) {
                    // console.log('SignalR: Rejoindre groupe validateur');
                    await signalRService.joinValidatorGroup(accountId);
                }
                
                // Toujours rejoindre le groupe des managers
                // console.log('SignalR: Rejoindre groupe manager');
                await signalRService.joinManagerGroup(accountId);

                // console.log(' SignalR: Tous les groupes rejoints');

            } catch (error) {
                // console.error('SignalR: Échec de connexion:', error);
                setIsConnected(false);
                setConnectionError(error.message);
            }
        };

        connectSignalR();

        // Nettoyage à la déconnexion
        return () => {
            const cleanup = async () => {
                try {
                    await signalRService.disconnect();
                } catch (error) {
                    // console.error('SignalR: Erreur lors du nettoyage:', error);
                }
            };
            cleanup();
        };
    }, [accountId, isValidator, isValidationManager]);

    // S'abonner aux notifications
    useEffect(() => {
        if (!accountId) return;

        const unsubscribe = signalRService.onNotification((notification) => {
            // console.log('SignalR: Nouvelle notification reçue:', notification);
            
            // Ajouter la notification à la liste
            const newNotification = {
                ...notification,
                IsRead: false,
                ReceivedAt: new Date().toISOString()
            };
            
            setNotifications(prev => [newNotification, ...prev.slice(0, 9)]);
            setUnreadCount(prev => prev + 1);
            
            // Afficher toast
            showNotificationToast(newNotification);
        });

        return () => {
            unsubscribe();
        };
    }, [accountId]);

    // Mettre à jour le compteur depuis le service
    useEffect(() => {
        const interval = setInterval(() => {
            setUnreadCount(signalRService.getUnreadCount());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Afficher une notification toast
    const showNotificationToast = useCallback((notification) => {
        const config = {
            duration: 5000,
            position: 'top-right',
        };

        switch (notification.Type) {
            case 'NEW_VALIDATION_TASK':
                toast.success(notification.Title, {
                    description: notification.Message,
                    ...config,
                    action: {
                        label: 'Voir',
                        onClick: () => {
                            if (notification.ActionUrl) {
                                window.location.href = notification.ActionUrl;
                            }
                        }
                    }
                });
                break;

            case 'ORDER_APPROVED':
                toast.success(notification.Title, {
                    description: notification.Message,
                    ...config,
                    icon: '✅'
                });
                break;

            case 'ORDER_REJECTED':
                toast.error(notification.Title, {
                    description: notification.Message,
                    ...config,
                    icon: '❌'
                });
                break;

            case 'ACCOUNT_APPROVED':
                toast.success(notification.Title, {
                    description: notification.Message,
                    ...config,
                    icon: '✅'
                });
                break;

            case 'ACCOUNT_REJECTED':
                toast.error(notification.Title, {
                    description: notification.Message,
                    ...config,
                    icon: '❌'
                });
                break;

            case 'VALIDATION_PROGRESS':
                toast.info(notification.Title, {
                    description: notification.Message,
                    ...config
                });
                break;

            default:
                toast.info(notification.Title || 'Notification', {
                    description: notification.Message,
                    ...config
                });
        }
    }, []);

    const markAsRead = useCallback(async (notificationId) => {
        if (!accountId) return;

        try {
            await signalRService.markAsRead(notificationId, accountId);
            
            setNotifications(prev => 
                prev.map(notif => 
                    notif.Id === notificationId 
                        ? { ...notif, IsRead: true }
                        : notif
                )
            );
            
            setUnreadCount(prev => Math.max(0, prev - 1));
            
        } catch (error) {
            // console.error('Erreur lors du marquage comme lu:', error);
        }
    }, [accountId]);

    const markAllAsRead = useCallback(async () => {
        if (!accountId) return;

        try {
            for (const notification of notifications.filter(n => !n.IsRead)) {
                await signalRService.markAsRead(notification.Id, accountId);
            }
            
            setNotifications(prev => 
                prev.map(notif => ({ ...notif, IsRead: true }))
            );
            
            setUnreadCount(0);
            signalRService.resetUnreadCount();
            
            toast.success('Toutes les notifications ont été marquées comme lues');
            
        } catch (error) {
            // console.error('Erreur lors du marquage:', error);
        }
    }, [accountId, notifications]);

    const formatNotificationTime = (timestamp) => {
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (isNaN(date.getTime())) {
                return 'Date inconnue';
            }

            if (diffMins < 1) return 'À l\'instant';
            if (diffMins < 60) return `Il y a ${diffMins} min`;
            if (diffHours < 24) return `Il y a ${diffHours} h`;
            if (diffDays < 7) return `Il y a ${diffDays} j`;
            
            return date.toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Date inconnue';
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'NEW_VALIDATION_TASK':
                return '📋';
            case 'ORDER_APPROVED':
                return '✅';
            case 'ORDER_REJECTED':
                return '❌';
            case 'ACCOUNT_APPROVED':
                return '✅';
            case 'ACCOUNT_REJECTED':
                return '❌';
            case 'VALIDATION_PROGRESS':
                return '📊';
            default:
                return '🔔';
        }
    };

    const getNotificationClass = (type) => {
        switch (type) {
            case 'ORDER_APPROVED':
            case 'ACCOUNT_APPROVED':
                return 'text-success';
            case 'ORDER_REJECTED':
            case 'ACCOUNT_REJECTED':
                return 'text-danger';
            case 'NEW_VALIDATION_TASK':
                return 'text-primary';
            case 'VALIDATION_PROGRESS':
                return 'text-info';
            default:
                return 'text-secondary';
        }
    };

    const testConnection = async () => {
        if (!accountId) {
            toast.error('AccountId manquant pour tester la connexion');
            return;
        }

        try {
            await signalRService.connect(accountId);
            toast.success('Connexion SignalR établie avec succès');
        } catch (error) {
            toast.error('Échec du test de connexion SignalR', {
                description: error.message
            });
        }
    };

    return {
        notifications,
        unreadCount,
        isConnected,
        connectionError,
        markAsRead,
        markAllAsRead,
        formatNotificationTime,
        getNotificationIcon,
        getNotificationClass,
        testConnection
    };
};